import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StatusType, TaskType } from "App.types";
import { Button, Space } from "antd";
import API from "Api";
import { SoundOutlined } from "@ant-design/icons";
import styles from "./SentenceTask.module.scss";
import cx from "classnames";
import { SentenceTaskProps } from "./SentenceTask.type";
import { GERMAN_VOICES, successMessage } from "App.constants";
import { getSpellAudioUrl, getNonEnglishAudioUrl } from "App.helpers";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import { NotifyContext, UserContext } from "App";
import TaskPanel from "Components/TaskPanel";
import useSentencesAudios from "Hooks/useSentencesAudios";
import { isNumber } from "lodash";

const SpellerSelectTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { id, text, wordGroup, storyId, button },
  lesson,
  onTaskComplete,
  onNext,
  children,
  audio,
  setDictOpened,
  showGrammar,
  setTry,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [selectedAnswer, setSelectedAnswer] = useState<string>();

  const user = useContext(UserContext);
  const notifyApi = useContext(NotifyContext);

  // initial
  useEffect(() => {
    setStatus(StatusType.Editing);
  }, []);

  const onComplete = useCallback(
    (showMessage = true) => {
      setStatus(StatusType.Completed);

      if (showMessage) {
        notifyApi.success(successMessage);
      }

      onTaskComplete();
    },
    [onTaskComplete, notifyApi],
  );

  const spellAudios = useMemo(
    () =>
      [...text]
        .filter((l) => l.trim())
        .map((s) => new Audio(user?.isEnglish ? getSpellAudioUrl(s) : getNonEnglishAudioUrl(s, GERMAN_VOICES[0]))),
    [text, user?.isEnglish],
  );

  const sentencesForAudios = useMemo(() => wordGroup?.words.map((wg) => ({ text: wg, id: sentence.id })), [wordGroup?.words, sentence.id]);

  const audios = useSentencesAudios({ sentences: sentencesForAudios });

  const onWordGroupClick = (t: string, idx?: number) => () => {
    spellAudios.forEach((a) => a.pause());

    if (t === text) {
      audio?.play();
    } else {
      isNumber(idx) && audios[idx].play();
    }

    if (status === StatusType.Completed) {
      return;
    }

    setSelectedAnswer(t);

    if (text === t) {
      onComplete(false);
      return StatusType.Completed;
    }

    setTry();

    setStatus(StatusType.Error);

    API.event.save({ sentence: { id: sentence.id }, text: t, type: "mistake", task: TaskType.SpellerSelect, lesson: { id: lesson.id } });
    return StatusType.Error;
  };

  const onHintAnswer = () => {
    API.event.save({ text, type: "hint", task: TaskType.SpellerSelect, lesson: { id: lesson.id }, sentence: { id: sentence.id } });

    setTry(true);

    onWordGroupClick(text)();
  };

  const playByLetters = () => {
    spellAudios.forEach((a) => a.pause());

    spellAudios.forEach((a, idx) => {
      a.onended = () => {
        if (idx === spellAudios.length - 1 && status === StatusType.Completed) {
          audio?.play();
        } else {
          spellAudios[idx + 1]?.play();
        }
      };
    });

    spellAudios[0].play();
  };

  const onNextClick = () => {
    spellAudios.forEach((a) => a.pause());
    onNext();
  };

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.content, styles.content_autoHeight)}>
        <SwitchTransition>
          <CSSTransition key={`${status}`} timeout={500} classNames={"fade"}>
            <div className={styles.children} style={{ visibility: status === StatusType.Completed ? "visible" : "hidden" }}>
              {children}
            </div>
          </CSSTransition>
        </SwitchTransition>

        <div className={styles.bottom}>
          <Space wrap align={"center"} className={styles.select}>
            {wordGroup?.words?.map((t, idx) => (
              <Button
                className={cx({
                  [styles.btn_red]: selectedAnswer !== text && t === selectedAnswer,
                  [styles.btn_green]: selectedAnswer === text && t === selectedAnswer,
                })}
                key={t}
                onClick={onWordGroupClick(t, idx)}
              >
                {t}
              </Button>
            ))}
          </Space>
        </div>
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.SpellerSelect}
        sentId={id}
        storyId={storyId}
        onNext={onNextClick}
        isCompleted={status === StatusType.Completed}
        onHint={onHintAnswer}
        setDictOpened={setDictOpened}
        audio={audio}
        showGrammar={showGrammar}
        nextButton={button}
      >
        <Button type={"primary"} icon={<SoundOutlined />} shape={"round"} onClick={playByLetters}></Button>
      </TaskPanel>
    </div>
  );
};

export default SpellerSelectTask;
